import {Box, Popover, Typography} from '@mui/material';
import {t} from 'i18next';
import {forwardRef, useImperativeHandle, useState} from 'react';
import ArrButton from '../../../components/common/arr-button/ArrButton';
import {fieldToApiColumnMap} from '../../../components/doc-list/DocList';

export type Metadata = Record<string, string>;
export type MetadataMenuHandle = {
  open: (anchor: HTMLElement) => void;
  close: () => void;
};

type Props = {
  metadata: Metadata;
  onClose: () => void;
};

const MetadataMenu = forwardRef<MetadataMenuHandle, Props>(
  ({metadata, onClose}, ref) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const metadataEntries = Object.entries(fieldToApiColumnMap);
    const open = Boolean(anchorEl);

    useImperativeHandle(ref, () => ({
      open: (anchor) => setAnchorEl(anchor),
      close: () => {
        setAnchorEl(null);
        onClose?.();
      },
    }));

    const handleClose = () => {
      setAnchorEl(null);
      onClose?.();
    };
    return (
      <Popover
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
        transformOrigin={{vertical: 'top', horizontal: 'left'}}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: 295,
              height: 200,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              maxHeight: 200,
              ml: 0,
            },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'sticky',
            top: 0,
            zIndex: 1,
            flexShrink: 0,
          }}
          className="header"
        >
          <Typography
            className="typo-text label2_size header-text"
            data-testid={`meta-data-card-mata-data`}
          >
            {t('docList.grid.mataData')}
          </Typography>
          <ArrButton
            variant="text"
            title={t('common.close')}
            size="xs"
            dataTestId="close_button"
            className="close_button"
            onClick={handleClose}
          >
            {t('common.close')}
          </ArrButton>
        </Box>
        <Box
          className="metadata-body"
          sx={{
            overflowY: 'auto',
            flexGrow: 1,
            minHeight: 0,
            px: 2,
            py: 1,
          }}
        >
          {metadataEntries.map(([key, metadataKey]) => (
            <Box
              key={key}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'start',
                gap: 1,
                py: 0.5,
              }}
            >
              <Typography
                className="typo-text body4_size body_text"
                variant="body2"
                sx={{flex: '0 0 40%'}}
                data-testid={`meta-data-card-typography-${key}`}
              >
                {key}
                {':'}
              </Typography>
              <Typography
                className="typo-text body4_size body_text"
                variant="body2"
                sx={{flex: '1 1 60%', wordBreak: 'break-word'}}
                data-testid={`meta-data-card-typography-text-${key}`}
              >
                {metadata[metadataKey]}
              </Typography>
            </Box>
          ))}
        </Box>
      </Popover>
    );
  }
);

export default MetadataMenu;
